import { AddressString } from '~components/providers/types';

import { NetworkIds } from '../../rpcConfig';
import {
  BalFarm,
  BoostedFarm,
  ELPType,
  ENativeAsset,
  EOtherAsset,
  EVestaAsset,
  FraxFarm,
  VestaFarm,
} from '../../types';

import { arbitrumContractConfig } from './arbitrum';
import { arbitrumRinkebyContractConfig } from './arbitrum-rinkeby';

export const NULL_ADDRESS = '0x0000000000000000000000000000000000000000';

export const contractConfig: Record<NetworkIds, ContractAddresses> = {
  [NetworkIds.ARBITRUM_ONE]: arbitrumContractConfig,
  [NetworkIds.ARBITRUM_RINKEBY]: arbitrumRinkebyContractConfig,
};

export const CONTRACT_ADDRESS_LOOKUP_CFG = Object.entries(contractConfig).reduce(
  (acc, [networkId, contractConfig]) => {
    // eslint-disable-next-line prettier/prettier
    acc[networkId as unknown as NetworkIds] = Object.entries(contractConfig).reduce(
      (acc, [contractName, contractAddress]) => {
        acc[contractAddress.toLowerCase()] = contractName as ContractNames;
        return acc;
      },
      {} as Record<string, ContractNames>,
    );

    return acc;
  },
  {} as Record<NetworkIds, Record<string, ContractNames>>,
);

export type ContractNames = keyof ContractAddresses;

// can just use ELPType when accessing contracts, but does not work well when declaring types
const vstaEth = ELPType['VSTA-ETH'];
const vstFrax = ELPType['VST-FRAX'];
const vstUsdc = ELPType['VST-USDC'];
const vstUsdcDaiUsdt = ELPType['VST-USDC-DAI-USDT'];

// farm symbols
const FARM_VESTA_VSTA_ETH = VestaFarm['VSTA-ETH'];
const FARM_VESTA_VST_USDC = BoostedFarm['VST-USDC'];
const FARM_VESTA_VST_FRAX = VestaFarm['VST-FRAX'];
const FARM_FRAX_VST_FRAX = FraxFarm.VST_FRAX_GAUGE;
const FARM_BAL_4_POOL = BalFarm['VST-USDC-DAI-USDT'];
const V2_FARM_BAL_4_POOL = BalFarm['V2-VST-USDC-DAI-USDT'];

export enum EContractName {
  MultiCall = 'MultiCall',
  ActivePool = 'ActivePool',
  VaultManager = 'VaultManager',
  MultiVaultGetter = 'MultiVaultGetter',
  SortedTroves = 'SortedTroves',
  StabilityPoolManager = 'StabilityPoolManager',
  BorrowerOperations = 'BorrowerOperations',
  CollateralSurplusPool = 'CollateralSurplusPool',
  HintHelpers = 'HintHelpers',
  CommunityIssuance = 'CommunityIssuance',
  VestaParameters = 'VestaParameters',
  Whitelisting = 'Whitelisting',
  BalancerVault = 'BalancerVault',
  VestingVesta = 'VestingVesta',
  VestaNFT = 'VestaNFT',
  GLPFeeTracker = 'GLPFeeTracker',
  GMXFeeTracker = 'GMXFeeTracker',
  //vesting
  VestingV1 = 'VestingV1',
  VestingV2 = 'VestingV2',
  //Price
  PriceFeed = 'PriceFeed',
  VSTOracle = 'VSTOracle',
  //GMX GLP
  GLPManager = 'GLPManager',
  VestaGMXStaking = 'VestaGMXStaking',
  VestaGLPStaking = 'VestaGLPStaking',
  VestaGMXBonusStaking = 'VestaGMXBonusStaking',
  //Stability pool
  ETHStabilityPool = 'ETHStabilityPool',
  gOHMStabilityPool = 'gOHMStabilityPool',
  renBTCStabilityPool = 'renBTCStabilityPool',
  DPXStabilityPool = 'DPXStabilityPool',
  GMXStabilityPool = 'GMXStabilityPool',
  GLPStabilityPool = 'GLPStabilityPool',
  ARBStabilityPool = 'ARBStabilityPool',
  wstETHStabilityPool = 'wstETHStabilityPool',
  ChainlinkBAL = 'ChainlinkBAL',
  ChainlinkFXS = 'ChainlinkFXS',
  ChainlinkFRAX = 'ChainlinkFRAX',
  ChainlinkUSDC = 'ChainlinkUSDC',
  ChainlinkDAI = 'ChainlinkDAI',
  ChainlinkUSDT = 'ChainlinkUSDT',
  VestaInterestManager = 'VestaInterestManager',
  ETHInterestRate = 'ETHInterestRate',
  gOHMInterestRate = 'gOHMInterestRate',
  DPXInterestRate = 'DPXInterestRate',
  GMXInterestRate = 'GMXInterestRate',
  GLPInterestRate = 'GLPInterestRate',
  ARBInterestRate = 'ARBInterestRate',
  wstETHInterestRate = 'wstETHInterestRate',
  VestaDexTrader = 'VestaDexTrader',
  VestaSavingModule = 'VestaSavingModule',
  SavingModuleStabilityPool = 'SavingModuleStabilityPool',
}

export type ContractAddresses = {
  [EContractName.GLPManager]: AddressString;
  [EContractName.MultiCall]: AddressString;
  [ENativeAsset.VSTA]: AddressString;
  [ENativeAsset.VST]: AddressString;
  [ENativeAsset.bVSTA]: AddressString;
  [EVestaAsset.wstETH]: AddressString;
  [EVestaAsset.renBTC]: AddressString;
  [EVestaAsset.ARB]: AddressString;
  [EOtherAsset.WETH]: AddressString;
  [EVestaAsset.GMX]: AddressString;
  [EOtherAsset.vwGMX]: AddressString;
  [EVestaAsset.DPX]: AddressString;
  [EOtherAsset.vwDPX]: AddressString;
  // vault sGLP (GLP) approve
  [EVestaAsset.sGLP]: AddressString;
  // balance GLP
  [EOtherAsset.fsGLP]: AddressString;
  [EVestaAsset.ETH]: AddressString;
  [EOtherAsset.USDC]: AddressString;
  [EOtherAsset.DAI]: AddressString;
  [EOtherAsset.USDT]: AddressString;
  [EVestaAsset.gOHM]: AddressString;
  [EOtherAsset.FRAX]: AddressString;
  [EOtherAsset.FXS]: AddressString;
  [vstaEth]: AddressString;
  [vstFrax]: AddressString;
  [vstUsdc]: AddressString;
  [vstUsdcDaiUsdt]: AddressString;
  [EOtherAsset.STAKED_4_POOL]: AddressString;
  [EOtherAsset.BAL]: AddressString;
  [EContractName.ActivePool]: AddressString;
  [EContractName.VaultManager]: AddressString;
  [EContractName.MultiVaultGetter]: AddressString;
  [EContractName.PriceFeed]: AddressString;
  [EContractName.SortedTroves]: AddressString;
  [EContractName.StabilityPoolManager]: AddressString;
  [EContractName.ETHStabilityPool]: AddressString;
  [EContractName.renBTCStabilityPool]: AddressString;
  [EContractName.gOHMStabilityPool]: AddressString;
  [EContractName.DPXStabilityPool]: AddressString;
  [EContractName.GMXStabilityPool]: AddressString;
  [EContractName.GLPStabilityPool]: AddressString;
  [EContractName.ARBStabilityPool]: AddressString;
  [EContractName.wstETHStabilityPool]: AddressString;
  [EContractName.BorrowerOperations]: AddressString;
  [EContractName.CollateralSurplusPool]: AddressString;
  [EContractName.HintHelpers]: AddressString;
  [EContractName.CommunityIssuance]: AddressString;
  [EContractName.VestaParameters]: AddressString;
  [EContractName.Whitelisting]: AddressString;
  [EContractName.BalancerVault]: AddressString;
  [EContractName.VestingVesta]: AddressString;
  [EContractName.VestaNFT]: AddressString;
  [EContractName.VestingV1]: AddressString;
  [EContractName.VestingV2]: AddressString;
  [EContractName.VestaGMXStaking]: AddressString;
  [EContractName.GMXFeeTracker]: AddressString;
  [EContractName.VestaGMXBonusStaking]: AddressString;
  [EContractName.VSTOracle]: AddressString;
  [EContractName.VestaGLPStaking]: AddressString;
  [EContractName.GLPFeeTracker]: AddressString;
  [FARM_VESTA_VSTA_ETH]: AddressString;
  [FARM_VESTA_VST_FRAX]: AddressString;
  [FARM_FRAX_VST_FRAX]: AddressString;
  [FARM_BAL_4_POOL]: AddressString;
  [FARM_VESTA_VST_USDC]: AddressString;
  [V2_FARM_BAL_4_POOL]: AddressString;
  [EContractName.ChainlinkBAL]: AddressString;
  [EContractName.ChainlinkDAI]: AddressString;
  [EContractName.ChainlinkFRAX]: AddressString;
  [EContractName.ChainlinkFXS]: AddressString;
  [EContractName.ChainlinkUSDC]: AddressString;
  [EContractName.ChainlinkUSDT]: AddressString;
  [EContractName.VestaInterestManager]: AddressString;
  [EContractName.ETHInterestRate]: AddressString;
  [EContractName.gOHMInterestRate]: AddressString;
  [EContractName.DPXInterestRate]: AddressString;
  [EContractName.GMXInterestRate]: AddressString;
  [EContractName.GLPInterestRate]: AddressString;
  [EContractName.ARBInterestRate]: AddressString;
  [EContractName.wstETHInterestRate]: AddressString;
  [EContractName.VestaDexTrader]: AddressString;
  [EContractName.VestaSavingModule]: AddressString;
  [EContractName.SavingModuleStabilityPool]: AddressString;
};
